import type { LaneView, TrendsView } from '../../../api/work';
import { FlowRibbon } from './FlowRibbon';
import { InputPanel } from './InputPanel';
import { LaneRow } from './LaneRow';
import { OutcomePanel } from './OutcomePanel';
import { PIPELINE, laneTopY } from './pipelineGeometry';
import './PipelineDiagram.css';

/**
 * The pipeline diagram — input, four lanes, outcome, and the flow between.
 *
 * **Restored 2026-09-27, reported as "the team insights page still doesn't
 * include the diagram from the old implementation".** It did not: the
 * Insights tab rendered the same numbers as a stack of cards, and this is
 * the thing those numbers were designed to be read through.
 *
 * Ported from the predecessor's `PipelineView` and its twelve components.
 * Every field it needs was already in `/api/work/trends` — `input.by_lane`,
 * `outcome.by_lane`, per-lane `states` with `age_distribution`,
 * `assignees` and `rework_count`, `handoff_latencies`, `mean_cycle_days`
 * and `throughput.daily`. Nothing was added to the backend for it.
 *
 * **What it reads left to right.** A stacked bar of everything that entered
 * the window, split by lane; ribbons carrying each lane's share into its
 * row; the row itself as the workflow's states with dwell, age mix,
 * hand-off queues and who is holding the work; ribbons out; and a stacked
 * bar of what left. The ribbon widths are the shares, so a lane that takes
 * a lot and finishes little is visible as a shape before any number is
 * read.
 *
 * **Two things the predecessor drew and this does not.**
 *
 * `PullChainOverlay` needed `view.pull_chain`, which this instance's route
 * does not return — the field does not exist in `TrendsView`. Omitted
 * rather than stubbed.
 *
 * `FindingChip` needs findings carrying `lane_primary` and `confidence`.
 * When this was written `/api/findings` was the only findings route, and
 * it is reflection's, carrying neither. 0.36.0 added
 * `/api/work/pulse/latest`, which serves pulse findings with both, so the
 * chips have something to place and are simply not built yet — recorded
 * in `machinai.app`'s `TODO.md`.
 *
 * **Fixed geometry, scaled by the viewport.** `PIPELINE` is a 1400×412
 * coordinate space tuned against real lane counts; the SVG scales it with
 * `viewBox` rather than recomputing at render size, which is what keeps
 * twelve components' worth of pixel constants honest.
 */

/**
 * The lane order and colours come from the data, not from a constant.
 *
 * The predecessor hardcoded `['td', 'sup', 'proj', 'core']` and reached into
 * a `LANE_COLORS` map keyed by the same four. Which lanes exist is
 * deployment configuration (`work.trends.lanes`), and the route already
 * returns them in configured order with each lane's own `color` — so a
 * deployment with different lanes drew nothing under the old code, and
 * needs no change under this.
 */
interface Segment {
  lane: string;
  color: string;
  count: number;
  yStart: number;
  yEnd: number;
}

/**
 * Stack the lane counts into a bar, returning each lane's y-range.
 *
 * Shares come from the route but the bar is laid out from counts, so a
 * rounding difference cannot make the segments overflow the bar: each
 * segment's height is rounded independently and the offsets accumulate,
 * so the stack is at most a pixel short rather than a pixel long.
 */
function stack(
  lanes: LaneView[],
  byLane: Record<string, number>,
  total: number,
  barHeight: number,
): Segment[] {
  let offset = 0;
  const top = PIPELINE.pipelineTop + PIPELINE.inputBarTopY;
  return lanes.map((lane) => {
    const count = byLane[lane.category] ?? 0;
    const h = Math.round((count / (total || 1)) * barHeight);
    const seg: Segment = {
      lane: lane.category,
      color: lane.color,
      count,
      yStart: top + offset,
      yEnd: top + offset + h,
    };
    offset += h;
    return seg;
  });
}

function counts(byLane: Array<{ lane: string; count: number }>) {
  return Object.fromEntries(byLane.map((b) => [b.lane, b.count]));
}

export function PipelineDiagram({ view }: { view: TrendsView }) {
  const lanes = view.lanes;
  const inputSegments = stack(
    lanes,
    counts(view.input.by_lane),
    view.input.total,
    PIPELINE.inputBarHeight,
  );
  const outcomeSegments = stack(
    lanes,
    counts(view.outcome.by_lane),
    view.outcome.total,
    PIPELINE.inputBarHeight,
  );

  // Half the lane height, capped: the ribbon should meet the lane, not
  // swallow it, and an unconstrained spread on a tall lane reads as a
  // block rather than a flow.
  const spread = Math.min(34, PIPELINE.laneHeight * 0.5);

  return (
    <div className="pipe-wrap">
      <svg
        className="pipe-svg"
        viewBox={`0 0 ${PIPELINE.width} ${PIPELINE.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={
          `Work pipeline: ${view.input.total} entered, ` +
          `${view.outcome.total} completed, across ` +
          `${view.lanes.length} lanes over ${view.window_days} days`
        }
      >
        <InputPanel
          x={PIPELINE.inputX}
          y={PIPELINE.pipelineTop}
          total={view.input.total}
          segments={inputSegments.map((s) => ({
            lane: s.lane,
            color: s.color,
            count: s.count,
          }))}
        />

        {inputSegments.map((seg, i) => {
          const mid = laneTopY(i) + PIPELINE.laneHeight / 2;
          return (
            <FlowRibbon
              key={`inlet-${seg.lane}`}
              x1={PIPELINE.ribbonLeft}
              y1Top={seg.yStart}
              y1Bot={seg.yEnd}
              x2={PIPELINE.ribbonLeftEnd}
              y2Top={mid - spread}
              y2Bot={mid + spread}
              color={seg.color}
            />
          );
        })}

        {lanes.map((lane, i) => (
          <LaneRow
            key={lane.category}
            lane={lane}
            laneY={laneTopY(i)}
            manpower={view.effort.members}
            laneColors={Object.fromEntries(lanes.map((l) => [l.category, l.color]))}
          />
        ))}

        {outcomeSegments.map((seg, i) => {
          const mid = laneTopY(i) + PIPELINE.laneHeight / 2;
          return (
            <FlowRibbon
              key={`outlet-${seg.lane}`}
              x1={PIPELINE.ribbonRight}
              y1Top={mid - spread}
              y1Bot={mid + spread}
              x2={PIPELINE.ribbonRightEnd}
              y2Top={seg.yStart}
              y2Bot={seg.yEnd}
              color={seg.color}
            />
          );
        })}

        <OutcomePanel
          x={PIPELINE.outputX}
          y={PIPELINE.pipelineTop}
          total={view.outcome.total}
          segments={outcomeSegments.map((s) => ({
            lane: s.lane,
            color: s.color,
            count: s.count,
          }))}
        />
      </svg>
    </div>
  );
}
